/**
 * Immunities, weaknesses and resistances (Foundry: actor/data/iwr.ts): each carries a type,
 * exceptions, an optional custom definition, and derives the predicate a damage roll's options
 * are tested against. Labels follow Foundry's English strings, taken from the Snapshot's tables
 * when present.
 */
import type { ManualIWR } from "../character/model";
import type { EngineState } from "./state";
import type { PredicateStatement, RawPredicate } from "./predicate";
import { testPredicate } from "./predicate";
import { DAMAGE_TYPES, MATERIAL_DAMAGE_EFFECTS } from "./values";

export type IWRKind = "immunity" | "weakness" | "resistance";

/** An exception: a type name, or a custom definition with its label. */
export type IWRException = string | { definition: RawPredicate; label: string };

export interface IWRData {
  kind: IWRKind;
  type: string;
  /** Weaknesses and resistances only. */
  value: number | null;
  exceptions: IWRException[];
  /** Resistances only: types the value doubles against. */
  doubleVs: IWRException[];
  /** A predicate defining a custom type. */
  definition: RawPredicate | null;
  customLabel: string | null;
  /** The Entry that granted it. */
  source: string | null;
  sourceName: string | null;
  /** Weaknesses only: applies once as untyped damage rather than per damage instance. */
  applyOnce: boolean;
  /** Entered by hand on the Character (#22): the value as entered and the note; null when a Rule Element made it. */
  manual: { value: number | null; note: string | null } | null;
}

const MAGIC_TRADITIONS = ["arcane", "divine", "occult", "primal"];
const CONDITION_SLUGS = [
  "blinded", "broken", "clumsy", "concealed", "confused", "controlled", "cursebound", "dazzled", "deafened", "doomed", "drained", "dying",
  "encumbered", "enfeebled", "fascinated", "fatigued", "fleeing", "friendly", "frightened", "grabbed", "helpful", "hidden", "hostile",
  "immobilized", "indifferent", "invisible", "malevolence", "observed", "off-guard", "paralyzed", "persistent-damage", "petrified", "prone",
  "quickened", "restrained", "sickened", "slowed", "stunned", "stupefied", "unconscious", "undetected", "unfriendly", "unnoticed", "wounded",
];

/** Weaknesses to things that don't normally deal damage (Foundry: APPLY_ONCE_WEAKNESSES). */
export const APPLY_ONCE_WEAKNESSES = new Set([...MAGIC_TRADITIONS, "air", "arrow-vulnerability", "axe-vulnerability", "earth", "ghost-touch", "holy", "metal", "plant", "radiation", "salt-water", "salt", "spells", "unholy", "water", "wood"]);

export interface IWRInput {
  kind: IWRKind;
  type: string;
  value?: number | null | undefined;
  exceptions?: IWRException[] | undefined;
  doubleVs?: IWRException[] | undefined;
  definition?: RawPredicate | null | undefined;
  customLabel?: string | null | undefined;
  source?: string | null | undefined;
  sourceName?: string | null | undefined;
  applyOnce?: boolean | undefined;
  manual?: { value: number | null; note: string | null } | null | undefined;
}

export function createIWR(data: IWRInput): IWRData {
  return {
    kind: data.kind,
    type: data.type,
    value: data.value ?? null,
    exceptions: structuredClone(data.exceptions ?? []),
    doubleVs: structuredClone(data.doubleVs ?? []),
    definition: data.definition ? structuredClone(data.definition) : null,
    customLabel: data.type === "custom" ? (data.customLabel ?? null) : null,
    source: data.source ?? null,
    sourceName: data.sourceName ?? null,
    applyOnce: data.applyOnce ?? (data.kind === "weakness" && APPLY_ONCE_WEAKNESSES.has(data.type)),
    manual: data.manual ? { ...data.manual } : null,
  };
}

/**
 * The entries entered by hand (#22), placed in the actor's IWR block before any Rule Element runs,
 * as Foundry keeps `system.attributes.{immunities,weaknesses,resistances}` from the actor's
 * source: a rule that adds the same type merges with the higher value, `override` replaces it,
 * `remove` removes it. Two hand entries of one type merge here the same way. A type the
 * Snapshot's table does not know is kept and labelled by its slug.
 */
export function seedManualIWR(state: EngineState, entries: readonly ManualIWR[]): void {
  const attributes = state.actor.system.attributes;
  for (const entry of entries) {
    const property = entry.kind === "immunity" ? attributes.immunities : entry.kind === "weakness" ? attributes.weaknesses : attributes.resistances;
    const value = entry.kind === "immunity" ? null : Math.max(1, Math.trunc(entry.value ?? 1));
    const candidate = createIWR({ kind: entry.kind, type: entry.type, value, exceptions: entry.exceptions ?? [], doubleVs: entry.kind === "resistance" ? entry.doubleVs ?? [] : [], manual: { value, note: entry.note ?? null } });
    const current = property.find((iwr) => sameIWR(iwr, candidate, { ignoreExceptions: entry.kind === "immunity" && candidate.exceptions.length === 0 }));
    if (!current) {
      property.push(candidate);
      continue;
    }
    if (entry.kind !== "immunity" && (value ?? 0) > (current.value ?? 0)) {
      current.value = value;
      current.manual = candidate.manual;
    } else if (current.manual && !current.manual.note && candidate.manual?.note) current.manual.note = candidate.manual.note;
  }
}

/** Foundry's IWR#describe: the statements a type is recognised by in a damage roll's options. */
export function describeIWR(iwr: IWRData, type: IWRException): PredicateStatement[] {
  if (typeof type !== "string") return type.definition;
  switch (type) {
    case "air": case "alchemical": case "earth": case "light": case "metal": case "olfactory": case "prediction": case "radiation": case "time": case "visual": case "water": case "wood":
      return [`item:trait:${type}`];
    case "all-damage": return ["damage"];
    case "arcane": case "divine": case "occult": case "primal": return [{ or: [`item:trait:${type}`, `origin:action:trait:${type}`] }];
    case "area-damage": return ["area-damage"];
    case "arrow-vulnerability": return ["item:group:bow"];
    case "auditory": return ["item:trait:auditory"];
    case "axes": case "axe-vulnerability": return ["item:group:axe"];
    case "critical-hits": return ["check:outcome:critical-success"];
    case "custom": return iwr.definition ?? [];
    case "disease": return ["item:trait:disease"];
    case "emotion": return ["item:type:effect", "item:trait:emotion"];
    case "energy": case "physical": return [`damage:category:${type}`];
    case "fear-effects": return ["item:type:effect", "item:trait:fear"];
    case "ghost-touch": return [{ or: ["item:rune:property:astral", "item:rune:property:ghost-touch", "item:rune:property:greater-astral"] }];
    case "holy": return [{ or: ["origin:action:trait:holy", "item:trait:holy"] }];
    case "magic": return [{ or: ["origin:action:trait:impulse", "item:from-spell", "item:type:spell"] }];
    case "magical": return [{ or: ["item:magical", "origin:action:trait:magical", ...MAGIC_TRADITIONS.map((t) => `origin:action:trait:${t}`)] }];
    case "mental": return [{ or: ["damage:type:mental", { and: ["item:type:effect", "item:trait:mental"] }] }];
    case "non-magical": return [{ not: "item:magical" }];
    case "object-immunities":
      return [{ or: ["damage:type:bleed", "damage:type:mental", "damage:type:poison", "damage:type:spirit", "damage:type:vitality", "damage:type:void", "origin:action:trait:vitality", "origin:action:trait:void", { and: ["item:type:condition", { or: ["item:slug:doomed", "item:slug:drained", "item:slug:fatigued", "item:slug:paralyzed", "item:slug:sickened", "item:slug:unconscious"] }] }] }];
    case "persistent-damage": return [{ or: ["damage:category:persistent", { and: ["item:type:condition", "item:slug:persistent-damage"] }] }];
    case "precision": case "splash-damage": return [`damage:component:${type === "splash-damage" ? "splash" : "precision"}`];
    case "spells": case "damage-from-spells": return ["damage", { or: ["item:type:spell", "item:from-spell", "item:trait:impulse"] }];
    case "unarmed-attacks": return ["item:category:unarmed"];
    case "unholy": return [{ or: ["origin:action:trait:unholy", "item:trait:unholy"] }];
    case "weapons": return ["item:type:weapon", { not: "item:category:unarmed" }];
    default: {
      if ((DAMAGE_TYPES as readonly string[]).includes(type)) return [`damage:type:${type}`];
      if (CONDITION_SLUGS.includes(type)) return ["item:type:condition", `item:slug:${type}`];
      if ((MATERIAL_DAMAGE_EFFECTS as readonly string[]).includes(type)) {
        switch (type) {
          case "adamantine": return iwr.kind === "resistance" ? [{ or: ["damage:material:adamantine", "damage:material:keep-stone"] }] : ["damage:material:adamantine"];
          case "cold-iron": return iwr.kind === "weakness" ? [{ or: ["damage:material:cold-iron", "damage:material:sovereign-steel"] }] : ["damage:material:cold-iron"];
          case "duskwood": return [{ or: ["damage:material:duskwood", { and: ["self:mode:undead", "damage:material:peachwood"] }] }];
          case "silver": return iwr.kind === "weakness" ? [{ or: ["damage:material:silver", "damage:material:dawnsilver"] }] : ["damage:material:silver"];
          default: return [`damage:material:${type}`];
        }
      }
      return [`unhandled:${type}`];
    }
  }
}

/** The predicate a damage roll's options are tested against (Foundry: IWR#predicate). */
export function iwrPredicate(iwr: IWRData): RawPredicate {
  const typeStatements = describeIWR(iwr, iwr.type);
  const exceptions = iwr.exceptions.flatMap((exception): PredicateStatement[] => {
    const described = describeIWR(iwr, exception).filter((s) => s !== "damage");
    return described.length === 1 ? described : [{ and: described }];
  });
  return [...typeStatements, ...(exceptions.length === 0 ? [] : exceptions.length === 1 ? [{ not: exceptions[0]! }] : [{ nor: exceptions }])];
}

export function testIWR(iwr: IWRData, options: Set<string> | string[]): boolean {
  return testPredicate(iwrPredicate(iwr), options);
}

/** A resistance's value against a described instance of damage, doubled when a `doubleVs` matches. */
export function resistanceValueAgainst(iwr: IWRData, options: Set<string>): number {
  const value = iwr.value ?? 0;
  if (iwr.doubleVs.length === 0) return value;
  const statements = iwr.doubleVs.map((d) => {
    const described = describeIWR(iwr, d);
    return described.length === 1 ? described[0]! : { and: described };
  });
  return testPredicate(statements.length === 1 ? [statements[0]!] : [{ or: statements }], options) ? value * 2 : value;
}

const TABLE_BY_KIND: Record<IWRKind, string> = { immunity: "immunityTypes", weakness: "weaknessTypes", resistance: "resistanceTypes" };

/** The English label of a type: the Snapshot's table, else the type slug spelled out. */
export function iwrTypeLabel(state: EngineState | null, kind: IWRKind, type: IWRException, customLabel: string | null = null): string {
  if (typeof type !== "string") return state?.catalogue?.localize(type.label) ?? type.label;
  if (type === "custom") return customLabel ?? "custom";
  const table = state?.catalogue?.table(TABLE_BY_KIND[kind]);
  const fromTable = table?.[type];
  if (fromTable) return fromTable;
  return type.replace(/-/g, " ");
}

/** Whether a type is one Foundry's tables know for this kind. Without the tables, every type is accepted. */
export function isKnownIWRType(state: EngineState | null, kind: IWRKind, type: string): boolean {
  if (type === "custom") return true;
  const table = state?.catalogue?.table(TABLE_BY_KIND[kind]);
  return table ? type in table : true;
}

const FORMATS = {
  Simple: "{type} {value}",
  Exceptions: "{type} {value} (except {exceptions})",
  DoubleVs: "{type} {value} (double resistance vs. {doubleVs})",
  ExceptionsDoubleVs: "{type} {value} (except {exceptions}; double resistance vs. {doubleVs})",
};

function formatList(items: string[]): string {
  if (items.length <= 1) return items[0] ?? "";
  if (items.length === 2) return `${items[0]} or ${items[1]}`;
  return `${items.slice(0, -1).join(", ")}, or ${items[items.length - 1]}`;
}

/** Foundry's IWR#label: "fire 5 (except cold iron; double resistance vs. silver)". */
export function iwrLabel(state: EngineState | null, iwr: IWRData, withValue = iwr.value !== null): string {
  const type = iwrTypeLabel(state, iwr.kind, iwr.type, iwr.customLabel);
  const exceptions = formatList(iwr.exceptions.map((e) => iwrTypeLabel(state, iwr.kind, e)));
  const doubleVs = formatList(iwr.doubleVs.map((e) => iwrTypeLabel(state, iwr.kind, e)));
  const key = iwr.exceptions.length > 0 ? (iwr.doubleVs.length > 0 ? "ExceptionsDoubleVs" : "Exceptions") : iwr.doubleVs.length > 0 ? "DoubleVs" : "Simple";
  const fullKey = `PF2E.Damage.IWR.CompositeLabel.${key}`;
  const localized = state?.catalogue?.localize(fullKey) ?? fullKey;
  const template = localized === fullKey ? FORMATS[key] : localized;
  const value = withValue ? String(iwr.value ?? "") : "";
  return template.replace(/\{(\w+)\}/g, (whole, name: string) => ({ type, value, exceptions, doubleVs } as Record<string, string>)[name] ?? whole).replace(/\s+/g, " ").trim();
}

function isDeepEqual(a: unknown, b: unknown): boolean {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

/** Whether two IWR entries are the same for merging purposes (type, exceptions, doubleVs, definition). */
export function sameIWR(a: IWRData, b: IWRData, { ignoreExceptions = false } = {}): boolean {
  return a.type === b.type && (ignoreExceptions || isDeepEqual(a.exceptions, b.exceptions)) && isDeepEqual(a.doubleVs, b.doubleVs) && isDeepEqual(a.definition, b.definition);
}
